import React, { useRef, useState } from "react";
import {
  AlertCircle,
  Circle,
  Loader2,
  Monitor,
  MonitorIcon,
  MonitorOff,
} from "lucide-react";
import ControlBar from "./ControlBarOption";
import RemoteVideo from "./RemoteVideo";
import "./VideoConsultation.scss";
import { message } from "antd";
import ConsultationHeader from "./ConsultationHeader";
import { set } from "date-fns";
import { createPortal } from "react-dom";

import ConsultationSidePanel from "./consultation-details/ConsultationSidePanel";
import ConformModal from "../../../../components/Modals/Reusable Modals/ConformModal";

const VideoConsultation = ({
  config,
  role = "Patient",
  headerData,
  onLeave,
  onJoin,
  onPipChange,
  isFloating = false,
  floatingMode,
  floatingViewMode = "single",
  onFloatingHeightChange,
}) => {
  const [participantCount, setParticipantCount] = React.useState(1);
  const [audioDevices, setAudioDevices] = React.useState([]);
  const [videoDevices, setVideoDevices] = React.useState([]);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showUserLeftModal, setShowUserLeftModal] = useState(false);
  const [leftUserName, setLeftUserName] = useState("");
  const [showUserJoinedModal, setShowUserJoinedModal] = useState(false);
  const [joinedUserName, setJoinedUserName] = useState("");
  const isLeavingRef = useRef(false);
  const [participants, setParticipants] = React.useState([]);
  const zoomClientRef = useRef(null);
  const streamRef = useRef(null);
  const originalVideoParentRef = useRef(null);
  const pipOriginalParentRef = useRef(null);
  const pipGalleryContainerRef = useRef(null);

  const [client, setClient] = React.useState(null);
  const [stream, setStream] = React.useState(null);
  const [mediaState, setMediaState] = React.useState({
    video: false,
    audio: false,
    screen: false,
  });
  const [audioDisabled, setAudioDisabled] = useState(false);
  const [isInitializing, setIsInitializing] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeSharingUserId, setActiveSharingUserId] = React.useState(null);
  const [sharerName, setSharerName] = React.useState("");
  const [pipPosition, setPipPosition] = React.useState({ x: 10, y: 10 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const [patientInfo, setPatientInfo] = React.useState({ name: "", id: "" });
  const [browserInfo, setBrowserInfo] = React.useState({
    isWebCodecs: false,
    isSafari: false,
    isIOS: false,
  });
  const [screenSubscribed, setScreenSubscribed] = React.useState(false);
  const [showSidePanel, setShowSidePanel] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("info");
  const [isPipMode, setIsPipMode] = React.useState(false);
  const [pipProp, setPipProp] = React.useState(null);

  const shareContainerRef = React.useRef(null);
  const screenShareVideoRef = React.useRef(null);
  const screenShareCanvasRef = React.useRef(null);
  const shareElementRef = React.useRef(null);
  const pipShareContainerRef = React.useRef(null);
  const pipRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const pipWindowRef = React.useRef(null);
  const videoLayoutsContainerRef = React.useRef(null);
  const pipContainerRef = React.useRef(null);
  const [pipMountNode, setPipMountNode] = React.useState(null);
  const [pipVideoMountNode, setPipVideoMountNode] = React.useState(null);
  const [isPipTransitioning, setIsPipTransitioning] = React.useState(false);
  const [localScreenStream, setLocalScreenStream] = React.useState(null);
  const isScreenSharingRef = React.useRef(false); // Track screen sharing state
  const isSubscribingRef = React.useRef(false);
  const subscriptionTimeoutRef = React.useRef(null);
  const currentSharingUserIdRef = React.useRef(null);

  const isDoctor = role === "Web_Doctor";
  const canShareScreen = isDoctor;
  const canLeave = isDoctor;

  React.useEffect(() => {
    streamRef.current = stream;
  }, [stream]);
  // Detect browser capabilities
  React.useEffect(() => {
    const detectBrowser = () => {
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const hasWebCodecs = "VideoEncoder" in window && "VideoDecoder" in window;

      setBrowserInfo({
        isWebCodecs: hasWebCodecs,
        isSafari,
        isIOS,
      });
    };

    detectBrowser();
  }, []);

  React.useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
      setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
    });
  }, []);

  // Snap to corners function
  const snapToCorner = (
    x,
    y,
    pipWidth,
    pipHeight,
    containerWidth,
    containerHeight
  ) => {
    const margin = 10;
    const corners = [
      { x: margin, y: margin },
      { x: containerWidth - pipWidth - margin, y: margin },
      { x: margin, y: containerHeight - pipHeight - margin },
      {
        x: containerWidth - pipWidth - margin,
        y: containerHeight - pipHeight - margin,
      },
    ];

    let closestCorner = corners[0];
    let minDistance = Number.MAX_SAFE_INTEGER;

    corners.forEach((corner) => {
      const distance = Math.sqrt(
        Math.pow(corner.x - x, 2) + Math.pow(corner.y - y, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestCorner = corner;
      }
    });

    return closestCorner;
  };

  // PIP Drag handlers
  const handleMouseDown = (e) => {
    if (!pipRef.current) return;
    const rect = pipRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = React.useCallback(
    (e) => {
      if (!isDragging || !pipRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();
      let newX = e.clientX - containerRect.left - dragOffset.x;
      let newY = e.clientY - containerRect.top - dragOffset.y;
      const margin = 10;
      newX = Math.max(
        margin,
        Math.min(containerRect.width - pipRect.width - margin, newX)
      );
      newY = Math.max(
        margin,
        Math.min(containerRect.height - pipRect.height - margin, newY)
      );
      setPipPosition({ x: newX, y: newY });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = React.useCallback(() => {
    if (!isDragging || !pipRef.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const pipRect = pipRef.current.getBoundingClientRect();
    const snappedPosition = snapToCorner(
      pipPosition.x,
      pipPosition.y,
      pipRect.width,
      pipRect.height,
      containerRect.width,
      containerRect.height
    );
    setPipPosition(snappedPosition);
    setIsDragging(false);
  }, [isDragging, pipPosition]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    const updatePipPosition = () => {
      if (!pipRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();
      const margin = 10;
      const defaultPosition = {
        x: margin,
        y: margin,
      };
      setPipPosition(defaultPosition);
    };
    setTimeout(updatePipPosition, 100);
    window.addEventListener("resize", updatePipPosition);
    return () => window.removeEventListener("resize", updatePipPosition);
  }, []);

  React.useEffect(() => {
    // Reset pip position when floating mode changes
    if (floatingMode) {
      const margin = 10;
      setPipPosition({ x: margin, y: margin });
    }
  }, [floatingMode]);

  React.useEffect(() => {
    const count = participants.length;
    setParticipantCount(count);
  }, [participants]);

  // Height notification for floating normal mode — must live here (before any
  // early returns) to satisfy React's rules-of-hooks.
  const SLOT_HEIGHT = 197; // px: 350px wide @ 16:9
  const CONTROL_BAR_HEIGHT = 58; // px: approximate control bar
  const lastNotifiedHeightRef = React.useRef(null);
  React.useEffect(() => {
    if (!isFloating || floatingMode !== "normal" || !onFloatingHeightChange)
      return;

    // Derive selfId locally — `userId` is declared after early returns so we
    // cannot reference it here (temporal dead zone).
    const selfId = client?.getCurrentUserInfo()?.userId;

    const isSingleViewMode = floatingViewMode !== "multi";
    const isScreenShareActive =
      activeSharingUserId !== null || mediaState.screen;
    const remoteCount = participants.filter((p) => p.userId !== selfId).length;

    let slotCount = 0;
    if (isScreenShareActive) slotCount += 1;
    slotCount += Math.min(remoteCount, 3 - slotCount);

    // Multi-view: self is always appended if there is room
    if (!isSingleViewMode && selfId && slotCount < 3) {
      const hasSelf = participants.some((p) => p.userId === selfId);
      if (hasSelf) slotCount += 1;
    }

    // Single-view with no remotes and no share → show self as fallback (1 slot)
    if (slotCount === 0) slotCount = 1;

    const contentHeight = slotCount * SLOT_HEIGHT + CONTROL_BAR_HEIGHT;
    if (lastNotifiedHeightRef.current !== contentHeight) {
      lastNotifiedHeightRef.current = contentHeight;
      onFloatingHeightChange(contentHeight);
    }
  }, [
    isFloating,
    floatingMode,
    floatingViewMode,
    participants,
    activeSharingUserId,
    mediaState.screen,
    client,
    onFloatingHeightChange,
  ]);

  React.useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData")) || {};
    const storedName = userData.profileName || "guest";
    const storedId = userData.userId || "0000";
    if (storedName && storedId) {
      setPatientInfo({ name: storedName, id: storedId });
    }
  }, []);

  React.useEffect(() => {
    const isScreenShareActive =
      activeSharingUserId !== null || mediaState.screen;
    let mirrorAnimationFrame;
    let mirrorCanvas;
    console.log("!@#", {
      isScreenShareActive,
      activeSharingUserId,
      shareElementRef,
      mediaStateScreen: mediaState.screen,
    });
    if (isScreenShareActive && shareElementRef.current) {
      const targetContainer = isPipMode
        ? pipShareContainerRef.current
        : shareContainerRef.current;

      const timeoutId = setTimeout(() => {
        if (!targetContainer || !shareElementRef.current) return;

        const isCanvas = shareElementRef.current.tagName === "CANVAS";

        if (isPipMode && isCanvas) {
          // Leave the original canvas in the main window to prevent breaking the drawing context
          if (
            pipOriginalParentRef.current &&
            shareElementRef.current.parentNode !== pipOriginalParentRef.current
          ) {
            pipOriginalParentRef.current.appendChild(shareElementRef.current);
          }

          // Create a mirror canvas in the PiP container
          targetContainer.innerHTML = "";
          mirrorCanvas = document.createElement("canvas");
          mirrorCanvas.style.width = "100%";
          mirrorCanvas.style.height = "100%";
          mirrorCanvas.style.objectFit = "contain";
          mirrorCanvas.style.backgroundColor = "#000";
          targetContainer.appendChild(mirrorCanvas);

          const mirrorContext = mirrorCanvas.getContext("2d");

          const drawFrame = () => {
            if (mirrorContext && shareElementRef.current && mirrorCanvas) {
              // Sync dimensions if they change or are initialized
              if (
                mirrorCanvas.width !== shareElementRef.current.width ||
                mirrorCanvas.height !== shareElementRef.current.height
              ) {
                mirrorCanvas.width = shareElementRef.current.width;
                mirrorCanvas.height = shareElementRef.current.height;
              }
              // Only draw if dimensions are valid
              if (mirrorCanvas.width > 0 && mirrorCanvas.height > 0) {
                mirrorContext.drawImage(
                  shareElementRef.current,
                  0,
                  0,
                  mirrorCanvas.width,
                  mirrorCanvas.height
                );
              }
            }
            mirrorAnimationFrame = requestAnimationFrame(drawFrame);
          };
          drawFrame();
        } else {
          // Standard physical move (for videos or when returning to main view)
          if (shareElementRef.current.parentNode !== targetContainer) {
            targetContainer.innerHTML = "";
            targetContainer.appendChild(shareElementRef.current);
            shareElementRef.current.style.visibility = "visible";

            if (shareElementRef.current.tagName === "VIDEO") {
              shareElementRef.current
                .play()
                .catch((e) => console.warn("Failed to play video:", e));
            }
          }
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        if (mirrorAnimationFrame) {
          cancelAnimationFrame(mirrorAnimationFrame);
        }
        if (mirrorCanvas && mirrorCanvas.parentNode) {
          mirrorCanvas.remove();
        }
      };
    }
  }, [
    isPipMode,
    activeSharingUserId,
    mediaState.screen,
    isFloating,
    floatingMode,
    floatingViewMode,
  ]);

  // Cleanup screen share elements
  const cleanupScreenShareElements = React.useCallback(() => {
    if (screenShareVideoRef.current) {
      try {
        screenShareVideoRef.current.srcObject = null;
        screenShareVideoRef.current.remove();
      } catch (e) {
        console.warn("Error removing video element:", e);
      }
      screenShareVideoRef.current = null;
    }

    if (screenShareCanvasRef.current) {
      try {
        screenShareCanvasRef.current.remove();
      } catch (e) {
        console.warn("Error removing canvas element:", e);
      }
      screenShareCanvasRef.current = null;
    }

    if (shareElementRef.current && shareElementRef.current.parentNode) {
      try {
        shareElementRef.current.remove();
      } catch (e) {
        console.warn("Error removing share element:", e);
      }
      shareElementRef.current = null;
    }

    if (shareContainerRef.current) {
      shareContainerRef.current.innerHTML = "";
    }
    if (pipShareContainerRef.current) {
      pipShareContainerRef.current.innerHTML = "";
    }
  }, []);

  const subscribeToScreenShareInternal = React.useCallback(
    async (userId, streamInstance) => {
      const mainDocContainer =
        shareContainerRef.current || pipOriginalParentRef.current;
      if (!streamInstance || !mainDocContainer) {
        console.warn("Stream or container not ready for subscription");
        return;
      }

      // Check if already subscribed to this user
      if (currentSharingUserIdRef.current === userId) {
        console.log("⏭️ Already subscribed to this user, skipping");
        return;
      }

      // Prevent duplicate subscriptions
      if (isSubscribingRef.current) {
        console.log("⏭️ Already subscribing, skipping duplicate call");
        return;
      }

      try {
        isSubscribingRef.current = true;
        currentSharingUserIdRef.current = userId;

        console.log(`📺 Subscribing to screen share from user ${userId}`);

        // Let the mirror effect handle the PiP container. Clean up the main containers.
        if (shareContainerRef.current) shareContainerRef.current.innerHTML = "";
        if (pipOriginalParentRef.current)
          pipOriginalParentRef.current.innerHTML = "";

        await new Promise((resolve) => setTimeout(resolve, 100));

        let shareElement;

        if (
          browserInfo.isWebCodecs &&
          !browserInfo.isSafari &&
          !browserInfo.isIOS
        ) {
          shareElement = document.createElement("video");
          shareElement.id = `screen-share-video-${userId}`;
          shareElement.autoplay = true;
          shareElement.playsInline = true;
          shareElement.muted = true;
        } else {
          shareElement = document.createElement("canvas");
          shareElement.id = `screen-share-canvas-${userId}`;
          shareElement.width = 1920;
          shareElement.height = 1080;
        }

        shareElement.style.width = "100%";
        shareElement.style.height = "100%";
        shareElement.style.objectFit = "contain";
        shareElement.style.backgroundColor = "#000";

        // Always append to a main window container first so Zoom SDK can attach successfully
        const targetContainerToAppend =
          shareContainerRef.current || pipOriginalParentRef.current;
        if (targetContainerToAppend) {
          targetContainerToAppend.appendChild(shareElement);
        }
        shareElementRef.current = shareElement;

        await streamInstance.startShareView(shareElement, userId);

        setScreenSubscribed(true);
      } catch (err) {
        console.error("❌ Error subscribing to screen share:", err);
        currentSharingUserIdRef.current = null; // Reset on error

        if (shareElementRef.current?.tagName === "VIDEO") {
          try {
            if (shareContainerRef.current)
              shareContainerRef.current.innerHTML = "";
            if (pipOriginalParentRef.current)
              pipOriginalParentRef.current.innerHTML = "";

            const canvas = document.createElement("canvas");
            canvas.id = `screen-share-canvas-${userId}`;
            canvas.width = 1920;
            canvas.height = 1080;
            canvas.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.objectFit = "contain";
            canvas.style.backgroundColor = "#000";

            const fallbackAppendContainer =
              shareContainerRef.current || pipOriginalParentRef.current;
            if (fallbackAppendContainer) {
              fallbackAppendContainer.appendChild(canvas);
            }
            shareElementRef.current = canvas;

            await streamInstance.startShareView(canvas, userId);
            currentSharingUserIdRef.current = userId; // Set after success
            setScreenSubscribed(true);
          } catch (fallbackErr) {
            console.error("❌ Canvas fallback also failed:", fallbackErr);
            if (shareContainerRef.current) {
              shareContainerRef.current.innerHTML =
                '<div class="text-white text-center p-4">Failed to load screen share</div>';
            }
            if (isPipMode && pipShareContainerRef.current) {
              pipShareContainerRef.current.innerHTML =
                '<div class="text-white text-center p-4">Failed to load screen share</div>';
            }
            setScreenSubscribed(false);
          }
        } else {
          if (shareContainerRef.current) {
            shareContainerRef.current.innerHTML =
              '<div class="text-white text-center p-4">Failed to load screen share</div>';
          }
          if (isPipMode && pipShareContainerRef.current) {
            pipShareContainerRef.current.innerHTML =
              '<div class="text-white text-center p-4">Failed to load screen share</div>';
          }
          setScreenSubscribed(false);
        }
      } finally {
        // Clear the flag after a delay
        subscriptionTimeoutRef.current = setTimeout(() => {
          isSubscribingRef.current = false;
        }, 1000);
      }
    },
    [browserInfo, isPipMode]
  );

  const unsubscribeFromScreenShare = React.useCallback(async () => {
    const currentStream = streamRef.current;

    if (!currentStream) return;
    try {
      console.log("🛑 Unsubscribing from screen share");
      await currentStream.stopShareView();
      cleanupScreenShareElements();
      setScreenSubscribed(false);
      currentSharingUserIdRef.current = null; // Reset tracking
      console.log("✅ Successfully unsubscribed from screen share");
    } catch (err) {
      console.error("Error unsubscribing from screen share:", err);
      // Force cleanup anyway
      cleanupScreenShareElements();
      setScreenSubscribed(false);
      currentSharingUserIdRef.current = null;
    }
  }, [cleanupScreenShareElements]);

  React.useEffect(() => {
    let mounted = true;
    const isMobileBrowser = () => {
      const ua = navigator.userAgent.toLowerCase();
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        ua
      );
    };

    const getDeviceInfo = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isMobile = isMobileBrowser();

      let platform = "desktop";
      if (/iphone|ipod/.test(ua)) platform = "ios-mobile";
      else if (/ipad/.test(ua)) platform = "ios-tablet";
      else if (/android/.test(ua)) {
        platform = /mobile/.test(ua) ? "android-mobile" : "android-tablet";
      }

      return {
        isMobile,
        platform,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        userAgent: navigator.userAgent,
      };
    };
    const initializeZoom = async () => {
      try {
        setIsInitializing(true);
        setError(null);

        // Only create client once
        if (zoomClientRef.current) {
          console.log("Zoom client already exists, skipping initialization");
          return;
        }

        if (!window.isSecureContext) {
          throw new Error("Secure context (HTTPS) required for video calls.");
        }

        if (!navigator.mediaDevices) {
          throw new Error("Media devices not supported.");
        }

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          console.log("iOS detected - ensuring media permissions");
          try {
            const testStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            testStream.getTracks().forEach((track) => track.stop());
          } catch (iosError) {
            console.warn("iOS media permission test failed:", iosError);
          }
        }

        console.log("Initializing Zoom for role:", role);
        const ZoomVideo = (await import("@zoom/videosdk")).default;

        const zoomClient = ZoomVideo.createClient();
        zoomClientRef.current = zoomClient;
        console.log("Zoom client created");

        if (!mounted) return;

        zoomClient.on("connection-change", (payload) => {
          console.log("Connection state changed:", payload);
          if (payload.reason === "ended by host") {
            handleSessionEnd();
          }
        });

        await zoomClient.init("en-US", "Global", {
          patchJsMedia: true,
          leaveOnPageUnload: true,
        });
        console.log("Zoom initialized");

        if (!mounted) return;

        const displayName = role;
        const userContextData = getDeviceInfo();
        console.log("Zoom joined", JSON.stringify(userContextData));

        await zoomClient.join(
          config.sessionName,
          config.sessionToken,
          displayName,
          "",
          { devicename: "Web" }
        );
        if (!mounted) return;

        const mediaStream = zoomClient.getMediaStream();
        setClient(zoomClient);
        setStream(mediaStream);
        streamRef.current = mediaStream;

        const allUsers = zoomClient.getAllUser();
        const selfId = zoomClient.getSessionInfo().userId;

        const initialParticipants = allUsers.map((user) => ({
          userId: user.userId,
          displayName: user.displayName || "User",
          bVideoOn: user.bVideoOn || false,
          muted: user.muted !== false,
        }));

        setParticipants(initialParticipants);

        // Check for existing screen share
        const sharingUser = allUsers.find((u) => u.sharerOn === true);
        if (sharingUser && sharingUser.userId !== selfId) {
          console.log(
            "📺 Found existing screen share from:",
            sharingUser.userId
          );
          setActiveSharingUserId(sharingUser.userId);
          setSharerName(sharingUser.displayName || "User");
          setTimeout(
            () =>
              subscribeToScreenShareInternal(sharingUser.userId, mediaStream),
            1000
          );
        }

        // Initialize audio
        const initAudio = async () => {
          try {
            mediaStream
              .startAudio()
              .then(async () => {
                await mediaStream.muteAudio();
                setMediaState((prev) => ({ ...prev, audio: false }));
                console.log("✅ Audio initialized successfully");
              })
              .catch((audioErr) => {
                console.error("Failed to auto-start audio:", audioErr);
                if (
                  audioErr.errorCode === 6013 ||
                  audioErr.errorCode === 6010
                ) {
                  setAudioDisabled(true);
                }
              });
          } catch (err) {
            console.error("Audio init error:", err);
          }
        };
        initAudio();

        if (onJoin) onJoin();

        // Event listeners
        zoomClient.on("user-added", async (payload) => {
          const userInfo = zoomClientRef.current
            ? zoomClientRef.current.getUser(payload.userId)
            : null;
          console.log("User device info:", userInfo?.deviceName || "Not set");

          const users = Array.isArray(payload) ? payload : [payload];
          console.log("device type", payload);

          // Detection for participant joined alert
          const selfId = zoomClientRef.current?.getSessionInfo()?.userId;
          const remoteUsersAdded = users.filter((u) => u.userId !== selfId);

          if (remoteUsersAdded.length > 0) {
            const lastUser = remoteUsersAdded[remoteUsersAdded.length - 1];
            setJoinedUserName(lastUser.displayName || "User");
            setShowUserJoinedModal(true);
          }

          setParticipants((prev) => {
            let participants = [...prev];
            users.forEach((user) => {
              if (!participants.find((p) => p.userId === user.userId)) {
                participants.push({
                  userId: user.userId,
                  displayName: user.displayName || "User",
                  bVideoOn: user.bVideoOn || false,
                  muted: user.muted !== false,
                  isDisconnected: false,
                  isPhoneUser: user.isPhoneUser || false,
                });
              }
            });
            return participants;
          });
        });

        zoomClient.on("user-removed", (payload) => {
          console.log("user-removed event payload:", payload);
          const users = Array.isArray(payload) ? payload : [payload];

          // Detection for participant disconnection alert
          const selfId = zoomClientRef.current?.getSessionInfo()?.userId;
          const remoteUsersRemoved = users.filter((u) => u.userId !== selfId);

          if (remoteUsersRemoved.length > 0) {
            const lastUser = remoteUsersRemoved[remoteUsersRemoved.length - 1];
            setLeftUserName(lastUser.displayName || "User");
            setShowUserLeftModal(true);
          }

          setParticipants((prev) =>
            prev.filter((p) => !users.find((u) => u.userId === p.userId))
          );
        });

        zoomClient.on("peer-video-state-change", async (payload) => {
          const { action, userId } = payload;
          setParticipants((prev) =>
            prev.map((p) =>
              p.userId === userId ? { ...p, bVideoOn: action === "Start" } : p
            )
          );
        });

        zoomClient.on("user-updated", (payload) => {
          const users = Array.isArray(payload) ? payload : [payload];
          setParticipants((prev) =>
            prev.map((p) => {
              const updatedUser = users.find((u) => u.userId === p.userId);
              if (updatedUser) {
                return {
                  ...p,
                  bVideoOn:
                    updatedUser.bVideoOn !== undefined
                      ? updatedUser.bVideoOn
                      : p.bVideoOn,
                  muted:
                    updatedUser.muted !== undefined
                      ? updatedUser.muted
                      : p.muted,
                  isDisconnected: updatedUser.isInFailover === true,
                };
              }
              return p;
            })
          );
        });

        zoomClient.on("active-share-change", async (payload) => {
          const selfId = zoomClient.getSessionInfo().userId;
          console.log("🎯 active-share-change:", {
            event: payload,
            selfId: selfId,
            isSelfSharing: payload.userId === selfId,
            currentSharingUser: currentSharingUserIdRef.current,
          });

          try {
            if (payload.state === "Active") {
              console.log("✅ Active share detected, userId:", payload.userId);
              const sharingUser = zoomClient
                .getAllUser()
                .find((u) => u.userId === payload.userId);

              setActiveSharingUserId(payload.userId);
              setSharerName(sharingUser?.displayName || "User");

              if (payload.userId === selfId) {
                console.log("✅ This is your own share");
                setMediaState((prev) => ({ ...prev, screen: true }));
                isScreenSharingRef.current = true;
                return;
              }

              // Only subscribe if not already subscribed to this user
              if (currentSharingUserIdRef.current !== payload.userId) {
                console.log(
                  "📺 Subscribing to remote share from active-share-change..."
                );
                await subscribeToScreenShareInternal(
                  payload.userId,
                  mediaStream
                );
              } else {
                console.log("⏭️ Already subscribed to this user");
              }
            } else if (payload.state === "Inactive") {
              console.log("🛑 Share stopped by:", payload.userId);

              if (payload.userId === selfId) {
                console.log("🛑 Your own share stopped");
                isScreenSharingRef.current = false;
              }

              await unsubscribeFromScreenShare();
              setActiveSharingUserId(null);
              setSharerName("");
              setMediaState((prev) => ({ ...prev, screen: false }));
            }
          } catch (err) {
            console.error("⚠️ Error in active-share-change handler:", err);
          }
        });

        zoomClient.on("passively-stop-share", async (payload) => {
          console.log("🛑 passively-stop-share event:", payload);
          const selfId = zoomClient.getSessionInfo().userId;

          try {
            if (payload.userId === selfId) {
              console.log("🛑 Your screen share was passively stopped");

              // CRITICAL: Update all state synchronously
              isScreenSharingRef.current = false;
              setMediaState((prev) => ({ ...prev, screen: false }));
              setActiveSharingUserId(null);
              setSharerName("");

              // Cleanup elements
              cleanupScreenShareElements();

              // Show notification
              message.info({
                content: (
                  <div style={{ textAlign: "center" }}>
                    Screen sharing stopped
                  </div>
                ),
                className: "atdclass",
                style: { marginTop: "2vh", color: "#007acc" },
              });

              console.log("✅ Cleaned up after passive stop", {
                isScreenSharingRef: isScreenSharingRef.current,
                mediaStateScreen: false,
              });
            } else {
              // Someone else's share was passively stopped
              console.log("🛑 Remote screen share was passively stopped");
              await unsubscribeFromScreenShare();
              setActiveSharingUserId(null);
              setSharerName("");
              setMediaState((prev) => ({ ...prev, screen: false }));
            }
          } catch (err) {
            console.error("⚠️ Error handling passively-stop-share:", err);
            // Force cleanup anyway
            isScreenSharingRef.current = false;
            setMediaState((prev) => ({ ...prev, screen: false }));
            setActiveSharingUserId(null);
            setSharerName("");
            cleanupScreenShareElements();
          }
        });

        zoomClient.on("peer-share-state-change", async (payload) => {
          console.log("🎯 peer-share-state-change:", payload);
          const { userId, action } = payload;
          const selfId = zoomClient.getSessionInfo().userId;

          // Ignore our own share state changes
          if (userId === selfId) {
            console.log("Ignoring self peer-share-state-change");
            return;
          }

          try {
            if (action === "Start") {
              // Check if not already subscribing or subscribed
              console.log("peer states", {
                isSubscribingRef: isSubscribingRef.current,
                currentSharingUserIdRef: currentSharingUserIdRef.current,
                userId,
              });
              if (
                !isSubscribingRef.current &&
                currentSharingUserIdRef.current !== userId
              ) {
                console.log(
                  "📺 Peer started sharing - initiating subscription with delay"
                );

                const sharingUser = zoomClient.getAllUser()
                  ? zoomClient.getAllUser().find((u) => u.userId === userId)
                  : null;
                setActiveSharingUserId(userId);
                setSharerName(sharingUser?.displayName || "User");

                // Wait for stream to be ready before subscribing
                setTimeout(async () => {
                  // Double-check we're not already subscribed
                  if (currentSharingUserIdRef.current !== userId) {
                    console.log(
                      "⏰ Executing delayed subscription from peer-share-state-change"
                    );
                    await subscribeToScreenShareInternal(userId, mediaStream);
                  }
                }, 1000); // 1 second delay to ensure stream is ready
              } else {
                console.log(
                  "⏭️ Already subscribing/subscribed, skipping peer-share-state-change"
                );
              }
            } else if (action === "Stop") {
              console.log("🛑 Peer stopped sharing");
              await unsubscribeFromScreenShare();
              setActiveSharingUserId(null);
              setSharerName("");
              setMediaState((prev) => ({ ...prev, screen: false }));
            }
          } catch (err) {
            console.error("⚠️ Error in peer-share-state-change handler:", err);
          }
        });
        setIsInitializing(false);
      } catch (err) {
        console.error("Zoom initialization error:", err);
        if (mounted) {
          setError(err.reason || "Failed to join consultation.");
          setIsInitializing(false);
        }
      }
    };

    initializeZoom();

    return () => {
      mounted = false;
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
      }
      isSubscribingRef.current = false;
      currentSharingUserIdRef.current = null;
      const mediaStream = stream;
      const zoomClient = zoomClientRef.current;

      if (pipWindowRef.current) {
        pipWindowRef.current.close();
      }

      if (mediaStream) {
        try {
          mediaStream?.stopVideo();
          mediaStream?.stopAudio();
          if (mediaState.screen) {
            mediaStream?.stopShareScreen();
          }
          if (mediaStream?.stopShareView) {
            mediaStream?.stopShareView();
          }
        } catch (e) {
          console.error("Cleanup error:", e);
        }
      }
      if (zoomClient) {
        try {
          zoomClient.leave();
          zoomClientRef.current = null;
        } catch (e) {
          console.error("Leave error:", e);
        }
      }
      cleanupScreenShareElements();
    };
  }, []);
  // }, [config, role, onJoin]);

  const getPrimaryVideoElement = () => {
    let el = document.querySelector(
      ".screen-share-content video-player-container"
    );
    if (el) return { element: el, type: "screen" };
    el = document.querySelector(".main-view-container video-player-container");
    if (el) return { element: el, type: "remote" };
    el = document.querySelector(".participants-grid video-player-container");
    if (el) return { element: el, type: "gallery" };
    return null;
  };

  React.useEffect(() => {
    if (isPipMode && pipVideoMountNode && pipGalleryContainerRef.current) {
      // Delay transport to allow Zoom SDK to initialize custom elements in the main window
      const timer = setTimeout(() => {
        if (pipVideoMountNode && pipGalleryContainerRef.current) {
          pipVideoMountNode.appendChild(pipGalleryContainerRef.current);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isPipMode, pipVideoMountNode]);

  const leavePictureInPicture = () => {
    // Pre-move share element to a persistent DOM node so it doesn't get unmounted and break the stream
    if (shareElementRef.current && pipOriginalParentRef.current) {
      pipOriginalParentRef.current.appendChild(shareElementRef.current);
    }

    // Physically move the gallery back to the main window BEFORE unmounting
    if (pipOriginalParentRef.current && pipGalleryContainerRef.current) {
      pipOriginalParentRef.current.appendChild(pipGalleryContainerRef.current);
    }

    if (pipWindowRef.current) {
      pipWindowRef.current.close();
      pipWindowRef.current = null;
    }
    setPipMountNode(null);
    setPipVideoMountNode(null);
    setIsPipMode(false);
    if (onPipChange) onPipChange(false);
  };

  React.useEffect(() => {
    return () => {
      if (pipWindowRef.current) {
        leavePictureInPicture();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attempt to enter PiP automatically when tab is hidden or user switches apps
  // React.useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === "hidden" && !isPipMode && !isInitializing) {
  //       console.log("Tab hidden, attempting auto-PiP...");
  //       // Note: Browsers usually require a user gesture (click) to open a PiP window.
  //       // This may be blocked depending on the browser's security policy.
  //       enterPictureInPicture().catch((err) => {
  //         console.warn("Auto-PiP attempt blocked (User gesture required):", err);
  //       });
  //     }
  //   };

  //   document.addEventListener("visibilitychange", handleVisibilityChange);
  //   return () => {
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //   };
  // }, [isPipMode, isInitializing]);

  const enterPictureInPicture = async () => {
    if (
      window.document.pictureInPictureEnabled &&
      "documentPictureInPicture" in window
    ) {
      try {
        const pipWindow = await window.documentPictureInPicture.requestWindow({
          width: 360,
          height: 600,
        });
        pipWindowRef.current = pipWindow;
        setPipProp(pipWindow);
        // Setup styles
        pipWindow.document.body.style.margin = "0";
        pipWindow.document.body.style.padding = "0";
        pipWindow.document.body.style.height = "100vh";
        pipWindow.document.body.style.display = "flex";
        pipWindow.document.body.style.flexDirection = "column";
        pipWindow.document.body.style.backgroundColor = "#111";
        pipWindow.document.body.style.overflow = "hidden";

        // Copy styles
        Array.from(document.styleSheets).forEach((styleSheet) => {
          try {
            if (styleSheet.cssRules) {
              Array.from(styleSheet.cssRules).forEach((rule) => {
                const style = document.createElement("style");
                style.textContent = rule.cssText;
                pipWindow.document.head.appendChild(style);
              });
            }
          } catch (e) {}
        });
        Array.from(
          document.querySelectorAll('link[rel="stylesheet"], style')
        ).forEach((el) => {
          pipWindow.document.head.appendChild(el.cloneNode(true));
        });

        const rootNode = document.createElement("div");
        rootNode.className = "pip-window-root";
        rootNode.style.width = "100%";
        rootNode.style.height = "100%";
        rootNode.style.display = "flex";
        rootNode.style.flexDirection = "column";
        pipWindow.document.body.appendChild(rootNode);

        // Container for videos
        const videosNode = document.createElement("div");
        videosNode.className = "pip-video-area";
        videosNode.style.flex = "1";
        videosNode.style.overflowY = "auto";
        videosNode.style.overflowX = "hidden";
        rootNode.appendChild(videosNode);

        // Container for controls
        const controlsNode = document.createElement("div");
        controlsNode.className = "pip-controls-area";
        controlsNode.style.flexShrink = "0";
        rootNode.appendChild(controlsNode);

        // Add pagehide listener
        pipWindow.addEventListener("pagehide", () => {
          leavePictureInPicture();
        });

        // Tell React to render the portals
        setPipVideoMountNode(videosNode);
        setPipMountNode(controlsNode);

        // Pre-move share element to a persistent DOM node so it doesn't get unmounted and break the stream
        if (shareElementRef.current && pipOriginalParentRef.current) {
          pipOriginalParentRef.current.appendChild(shareElementRef.current);
        }

        setIsPipMode(true);
        if (onPipChange) onPipChange(true);
      } catch (error) {
        console.error(
          "Failed to enter Document Picture-in-Picture mode:",
          error
        );
        message.warning(
          "Failed to enter Picture-in-Picture. Please try again."
        );
      }
    } else {
      console.warn(
        "Document Picture-in-Picture is not supported by your browser."
      );
      message.warning("Picture-in-Picture is not supported by your browser.");
    }
  };

  const togglePictureInPicture = () => {
    if (isPipMode) {
      leavePictureInPicture();
    } else {
      enterPictureInPicture();
    }
  };

  // UPDATED: Subscribe to screen share with browser detection
  const subscribeToScreenShare = React.useCallback(
    async (userId) => {
      if (!stream || !shareContainerRef.current) {
        console.warn("Stream or container not ready for subscription");
        return;
      }
      await subscribeToScreenShareInternal(userId, stream);
    },
    [stream, subscribeToScreenShareInternal]
  );

  const handleSessionEnd = async () => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    console.log("🔚 Session ended");
    try {
      if (stream) {
        await stream.stopVideo();
        await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        console.log("leave true is called handleSessionEnd");
        await client.leave(true);
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      onLeave();
    }
  };

  const handleToggleVideo = async () => {
    if (!stream || !client) return;
    try {
      const selfId = client.getSessionInfo().userId;
      if (mediaState.video) {
        await stream.stopVideo();
        setMediaState((prev) => ({ ...prev, video: false }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, bVideoOn: false } : p))
        );
      } else {
        // await stream.startVideo();
        await stream.startVideo({ originalRatio: true });
        setMediaState((prev) => ({ ...prev, video: true }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, bVideoOn: true } : p))
        );
      }
    } catch (err) {
      console.error("Error toggling video:", err);
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Please allow permission for camera
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };

  const handleToggleAudio = async () => {
    if (!stream) return;
    try {
      const selfId = client?.getSessionInfo().userId;
      if (mediaState.audio) {
        await stream.muteAudio();
        setMediaState((prev) => ({ ...prev, audio: false }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, muted: true } : p))
        );
      } else {
        try {
          await stream.startAudio();
        } catch (e) {
          console.log("Audio already started");
        }
        await stream.unmuteAudio();
        setMediaState((prev) => ({ ...prev, audio: true }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, muted: false } : p))
        );
      }
    } catch (err) {
      console.error("Error toggling audio:", err);
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Please allow permission for audio
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };

  const handleScreenShare = async () => {
    console.log("handleScreenShare invoked", canShareScreen);
    setShowSidePanel(false);
    if (!canShareScreen) {
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Screen sharing is not available for patients
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
      return;
    }

    if (!stream || !client) {
      console.error("Stream or client not ready");
      return;
    }

    try {
      const selfId = client.getSessionInfo().userId;

      // STOPPING SCREEN SHARE
      if (mediaState.screen || isScreenSharingRef.current) {
        console.log("🛑 Stopping screen share (programmatic)...");

        try {
          // Stop the screen share stream
          await stream.stopShareScreen();
          console.log("✅ stopShareScreen completed");

          // Small delay to ensure event propagates
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Update state immediately
          isScreenSharingRef.current = false;
          setMediaState((prev) => ({ ...prev, screen: false }));
          setActiveSharingUserId(null);
          setSharerName("");

          // Cleanup elements
          cleanupScreenShareElements();

          console.log("✅ Screen share stopped successfully");
        } catch (stopErr) {
          console.error("❌ Error stopping screen share:", stopErr);
          // Force cleanup even if stop fails
          isScreenSharingRef.current = false;
          setMediaState((prev) => ({ ...prev, screen: false }));
          setActiveSharingUserId(null);
          setSharerName("");
          cleanupScreenShareElements();
        }

        return;
      }

      // STARTING SCREEN SHARE
      console.log("🔄 Starting screen share...");
      console.log("Browser info:", browserInfo);

      const allUsers = client.getAllUser();
      const someoneSharing = allUsers.some(
        (u) => u.sharerOn === true && u.userId !== selfId
      );

      if (someoneSharing) {
        message.info({
          content: (
            <div style={{ textAlign: "center" }}>
              Someone else is already sharing. Please wait.
            </div>
          ),
          className: "atdclass",
          style: { marginTop: "2vh", color: "#007acc" },
        });
        return;
      }

      // Cleanup any existing elements first
      cleanupScreenShareElements();

      // Small delay before creating new element
      await new Promise((resolve) => setTimeout(resolve, 100));

      let shareElement;

      if (
        browserInfo.isWebCodecs &&
        !browserInfo.isSafari &&
        !browserInfo.isIOS
      ) {
        console.log("Using VIDEO element for sharing");

        const video = document.createElement("video");
        video.id = "my-screen-share-video";
        video.height = 1080;
        video.width = 1920;
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: contain;
          position: absolute;
          top: 0;
          left: 0;
          visibility: hidden;
        `;

        document.body.appendChild(video);
        screenShareVideoRef.current = video;
        shareElement = video;
      } else {
        console.log("Using CANVAS element for sharing");

        const canvas = document.createElement("canvas");
        canvas.id = "my-screen-share-canvas";
        canvas.height = 1080;
        canvas.width = 1920;
        canvas.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: contain;
          position: absolute;
          top: 0;
          left: 0;
          visibility: hidden;
        `;

        document.body.appendChild(canvas);
        screenShareCanvasRef.current = canvas;
        shareElement = canvas;
      }

      console.log("📦 Created share element:", shareElement.tagName);

      // Start screen share
      await stream.startShareScreen(shareElement);
      console.log("✅ startShareScreen completed");

      // Update state immediately after starting
      isScreenSharingRef.current = true;
      setMediaState((prev) => ({ ...prev, screen: true }));
      setActiveSharingUserId(selfId);
      setSharerName("You");

      // Make element visible and register it to trigger the PiP mirroring effect
      shareElement.style.visibility = "visible";
      shareElementRef.current = shareElement;

      // Always append to a main window container first; the mirroring effect handles PiP transitions
      const targetContainer =
        shareContainerRef.current || pipOriginalParentRef.current;
      if (targetContainer) {
        targetContainer.appendChild(shareElement);
      }

      console.log(
        `✅ Screen share started successfully with ${shareElement.tagName}`
      );
    } catch (err) {
      console.error("❌ Screen share error:", err);

      // Reset state on error
      isScreenSharingRef.current = false;
      setMediaState((prev) => ({ ...prev, screen: false }));
      setActiveSharingUserId(null);
      setSharerName("");

      // Cleanup elements
      cleanupScreenShareElements();

      let errorMessage = "Failed to share screen";
      if (err.type === "INVALID_PARAMETERS") {
        errorMessage =
          "Screen sharing configuration error. Please refresh and try again.";
      } else if (
        err.type === "INSUFFICIENT_PRIVILEGES" ||
        (err.reason && err.reason.includes("permission"))
      ) {
        errorMessage = "Screen sharing permission denied by browser";
      } else if (err.type === "IMPROPER_MEETING_STATE") {
        errorMessage =
          "Session not ready for screen sharing. Please try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      message.info({
        content: <div style={{ textAlign: "center" }}>{errorMessage}</div>,
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };
  const handleLeave = async (isEndByDuplicate = false) => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    try {
      if (stream) {
        await stream.stopVideo();
        await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        await client.leave();
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      cleanupScreenShareElements();
      onLeave(isEndByDuplicate);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseModal(false);
    handleEndSession();
  };

  const handleEndSession = async () => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    try {
      if (stream) {
        if (mediaState.video) await stream.stopVideo();
        if (mediaState.audio) await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        console.log("leave true is called handleEndSession");
        await client.leave(true);
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      cleanupScreenShareElements();
      onLeave();
    }
  };

  const handleRemoveDisconnectedUser = async (userId) => {
    console.log(`🗑️ Removing disconnected user ${userId}`);
    try {
      if (isDoctor && client) {
        const currentUser = client.getCurrentUserInfo();
        if (currentUser.isHost || role === "Web_Doctor") {
          await client.removeUser(userId);
          console.log(`✅ User ${userId} removed from Zoom session`);
        }
      }
      setParticipants((prev) => prev.filter((p) => p.userId !== userId));
    } catch (error) {
      console.error(`❌ Error removing user ${userId}:`, error);
      setParticipants((prev) => prev.filter((p) => p.userId !== userId));
    }
  };

  if (error) {
    return (
      <div className="video-consultation">
        <div className="error-state">
          <div className="error-container">
            <div className="error-icon">
              <AlertCircle className="alert-icon" />
            </div>
            <h3 className="error-title">Connection Error</h3>
            <p className="error-message">{error}</p>
            <button onClick={onLeave} className="error-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isInitializing) {
    return (
      <div className="zoom-full-height">
        <div className="loading-state">
          <div className="loading-content">
            <div className="spinner"></div>
            <p className="loading-text">Initializing consultation...</p>
            <p className="loading-subtext">Setting up your video session</p>
          </div>
        </div>
      </div>
    );
  }

  const userId = client?.getCurrentUserInfo()?.userId;
  const selfId = client?.getSessionInfo()?.userId;
  const shouldShowScreenShareLayout = activeSharingUserId !== null;
  const otherParticipants = participants.filter((p) => p.userId !== userId);
  const isTwoUserLayout = participants.length === 2;

  const getGridClass = (count) => {
    if (count === 1) return "grid-1";
    if (count === 2) return "grid-2";
    if (count === 3) return "grid-3";
    if (count === 4) return "grid-4";
    if (count <= 6) return "grid-5-6";
    return "grid-7-plus";
  };

  const getHeaderInfo = () => {
    const isDoctor = role === "Web_Doctor";
    if (participantCount <= 1) {
      return null;
    }

    if (isDoctor) {
      return {
        name: headerData?.patientName || "Patient",
        age: headerData?.age,
        gender: headerData?.gender,
        consultationDetails: {
          patientName: headerData?.patientName,
          doctorName: headerData?.doctorDetails?.doctorName,
          date: headerData?.appointmentDate,
          time: headerData?.appointmentTime,
          modeOfConsultation: headerData?.modeOfConsultation,
          branch: headerData?.branchName,
        },
      };
    } else {
      return {
        name: headerData?.doctorName || "Doctor",
        age: headerData?.doctorDetails?.age,
        gender: headerData?.doctorDetails?.gender,
        consultationDetails: {
          doctorName: headerData?.doctorDetails?.doctorName,
          specialization: headerData?.doctorDetails?.specialization,
          date: headerData?.appointmentDate,
          time: headerData?.appointmentTime,
          modeOfConsultation: headerData?.modeOfConsultation,
          branch: headerData?.branchName,
        },
      };
    }
  };

  const headerInfo = getHeaderInfo();

  const renderPipGallery = () => {
    // Sort participants: remote users first, local user last
    const sortedParticipants = [...participants].sort((a, b) => {
      if (a.userId === userId) return 1;
      if (b.userId === userId) return -1;
      return 0;
    });

    const isScreenShareActive =
      activeSharingUserId !== null || mediaState.screen;

    return (
      <div className="pip-gallery-container">
        {isScreenShareActive && (
          <div
            className="pip-screen-share-item"
            style={{
              width: "100%",
              aspectRatio: "16/9",
              position: "relative",
              backgroundColor: "#000",
              marginBottom: "8px",
            }}
          >
            <div
              ref={pipShareContainerRef}
              className="pip-video-player-container"
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            ></div>
            <div
              className="sharing-indicator"
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                zIndex: 10,
                background: "rgba(0,0,0,0.6)",
                padding: "4px 8px",
                borderRadius: "4px",
                color: "white",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Monitor size={12} />
              <span>
                {sharerName}{" "}
                {sharerName === "You" ? "are sharing" : "is sharing"}
              </span>
            </div>
          </div>
        )}
        {stream &&
          sortedParticipants.map((participant, index) => (
            <div
              key={participant.userId || index}
              className="pip-participant-item"
            >
              <RemoteVideo
                stream={stream}
                userId={participant.userId}
                isVideoOn={
                  participant.userId === userId
                    ? mediaState.video
                    : participant.bVideoOn
                }
                isAudioOn={
                  participant.userId === userId
                    ? mediaState.audio
                    : !participant.muted
                }
                userName={
                  participant.userId === userId
                    ? `You`
                    : participant.displayName || "User"
                }
                userData={participant}
                isPip={true}
                pipWindow={pipProp}
                isDisconnected={participant.isDisconnected}
                onRemoveDisconnected={handleRemoveDisconnectedUser}
              />
            </div>
          ))}
      </div>
    );
  };

  //floating single view

  const renderSingleView = () => {
    if (!stream) return null;

    const isSingleViewMode = floatingViewMode !== "multi";
    const isScreenShareActive =
      activeSharingUserId !== null || mediaState.screen;

    const remoteParticipants = participants.filter((p) => p.userId !== userId);

    const MAX_SLOTS = 3;

    const slots = [];

    if (isScreenShareActive) {
      slots.push({ type: "screenshare" });
    }

    remoteParticipants.forEach((p) => {
      if (slots.length < MAX_SLOTS) {
        slots.push({ type: "remote", participant: p });
      }
    });

    if (slots.length === 0 && isSingleViewMode) {
      const selfParticipant = participants.find((p) => p.userId === userId);
      if (selfParticipant) {
        slots.push({ type: "self", participant: selfParticipant });
      } else {
        slots.push({ type: "waiting" });
      }
    }

    // Multi-view: add self at the end (if room)
    if (!isSingleViewMode && userId && slots.length < MAX_SLOTS) {
      const selfParticipant = participants.find((p) => p.userId === userId);
      if (selfParticipant) {
        slots.push({ type: "self", participant: selfParticipant });
      }
    }

    // Final fallback: if still nothing (multi-view, no one at all) show waiting
    if (slots.length === 0) {
      slots.push({ type: "waiting" });
    }

    // Notify parent of required content height
    // (moved to a dedicated useEffect to avoid calling setState during render)

    return (
      <div
        className="single-view-layout"
        style={{
          display: "flex",
          flexDirection: "column",
          background: "#111",
          width: "100%",
          overflowY: "auto",
          maxHeight: "calc(100% - 56px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {slots.map((slot, idx) => {
          if (slot.type === "screenshare") {
            return (
              <div
                key="screenshare"
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  position: "relative",
                  backgroundColor: "#000",
                  flexShrink: 0,
                }}
              >
                <div
                  ref={shareContainerRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
                {/* Sharing indicator */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "6px",
                    left: "6px",
                    zIndex: 10,
                    background: "rgba(0,0,0,0.6)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Monitor size={10} />
                  <span>
                    {sharerName}{" "}
                    {sharerName === "You" ? "are sharing" : "is sharing"}
                  </span>
                </div>
              </div>
            );
          }

          if (slot.type === "waiting") {
            return (
              <div
                key="waiting"
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  backgroundColor: "#1a1a1a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    color: "rgba(255,255,255,0.4)",
                    fontSize: "12px",
                  }}
                >
                  <div style={{ marginBottom: "6px", fontSize: "20px" }}>
                    🎥
                  </div>
                  Waiting for participant…
                </div>
              </div>
            );
          }

          // remote or self slot
          const { participant } = slot;
          const isSelf = slot.type === "self";
          return (
            <div
              key={participant.userId}
              style={{
                width: "100%",
                aspectRatio: "16/9",
                position: "relative",
                backgroundColor: "#1a1a1a",
                flexShrink: 0,
              }}
            >
              <RemoteVideo
                stream={stream}
                userId={participant.userId}
                isVideoOn={isSelf ? mediaState.video : participant.bVideoOn}
                isAudioOn={isSelf ? mediaState.audio : !participant.muted}
                userName={isSelf ? "You" : participant.displayName || "User"}
                userData={participant}
                isPip={true}
                isDisconnected={isSelf ? false : participant.isDisconnected}
                onRemoveDisconnected={handleRemoveDisconnectedUser}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderVideoLayouts = () => {
    if (isPipMode) return null;
    return (
      <React.Fragment>
        {shouldShowScreenShareLayout ? (
          <div className="screen-share-layout">
            <div className="screen-share-content">
              <div className="sharing-indicator">
                <Circle className="recording-icon" fill="currentColor" />
                <span className="sharer-name">
                  {sharerName}{" "}
                  {sharerName === "You"
                    ? "are sharing screen now"
                    : "is sharing screen"}
                </span>
              </div>
              <div ref={shareContainerRef} className="video-player-container">
                <div className="loading-state">
                  <Loader2 className="spinner" />
                  <p>Loading screen share...</p>
                </div>
              </div>
            </div>
            <div className="participants-carousel">
              <div className="carousel-container">
                {stream &&
                  userId &&
                  participants.map((participant, index) => (
                    <div
                      key={participant.userId || index}
                      className="participant-item"
                    >
                      <RemoteVideo
                        stream={stream}
                        userId={participant.userId}
                        isVideoOn={participant.bVideoOn}
                        isAudioOn={!participant.muted}
                        userName={
                          participant.userId === userId
                            ? `You`
                            : participant.displayName || "User"
                        }
                        userData={participant}
                        isDisconnected={participant.isDisconnected}
                        onRemoveDisconnected={handleRemoveDisconnectedUser}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : isTwoUserLayout ? (
          <div className="two-user-layout">
            {mediaState.screen && (
              <div className="self-sharing-indicator">
                <div className="pulse-dot"></div>
                <Monitor className="monitor-icon" />
                <span className="sharing-text">
                  You are sharing your screen
                </span>
              </div>
            )}
            <div className="main-view-container" ref={containerRef}>
              {stream && otherParticipants.length > 0 && (
                <RemoteVideo
                  stream={stream}
                  userId={otherParticipants[0].userId}
                  isVideoOn={otherParticipants[0].bVideoOn}
                  isAudioOn={!otherParticipants[0].muted}
                  userName={otherParticipants[0].displayName || "User"}
                  userData={otherParticipants[0]}
                  isDisconnected={otherParticipants[0].isDisconnected}
                  onRemoveDisconnected={handleRemoveDisconnectedUser}
                />
              )}
              {stream && userId && (
                <div
                  ref={pipRef}
                  className={`pip-container ${isDragging ? "dragging" : ""}`}
                  style={{
                    left: `${pipPosition.x}px`,
                    top: `${pipPosition.y}px`,
                  }}
                  onMouseDown={handleMouseDown}
                >
                  <RemoteVideo
                    stream={stream}
                    userId={userId}
                    isVideoOn={mediaState.video}
                    isAudioOn={mediaState.audio}
                    userName={`You`}
                    isPip={true}
                    className="pip-content"
                    userData={{}}
                    isDisconnected={false}
                    onRemoveDisconnected={() => {}}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="gallery-layout">
            {mediaState.screen && (
              <div className="gallery-sharing-indicator">
                <div className="pulse-dot"></div>
                <Monitor className="monitor-icon" />
                <span className="sharing-text">
                  You are sharing your screen
                </span>
              </div>
            )}
            <div className="gallery-container">
              {stream && userId && (
                <div
                  className={`participants-grid ${getGridClass(
                    participants.length
                  )}`}
                >
                  {participants.map((participant, index) => (
                    <div
                      key={participant.userId || index}
                      className="participant-grid-item"
                    >
                      <RemoteVideo
                        stream={stream}
                        userId={participant.userId}
                        isVideoOn={participant.bVideoOn}
                        isAudioOn={!participant.muted}
                        userName={
                          participant.userId === userId
                            ? `${role} (You)`
                            : participant.displayName || "User"
                        }
                        userData={participant}
                        isDisconnected={participant.isDisconnected}
                        onRemoveDisconnected={handleRemoveDisconnectedUser}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderControlBar = () => (
    <ControlBar
      mediaState={mediaState}
      onTogglePip={togglePictureInPicture}
      isPipMode={isPipMode}
      onToggleVideo={handleToggleVideo}
      onToggleAudio={handleToggleAudio}
      onLeave={handleLeave}
      onEndConsultation={isDoctor ? () => setShowCloseModal(true) : null}
      onToggleScreenShare={handleScreenShare}
      canShareScreen={isDoctor}
      canLeave={canLeave}
      role={role}
      mediaStream={stream}
      zmClient={client}
      isAudioDisabled={audioDisabled}
      showSidePanel={showSidePanel}
      onToggleSidePanel={(activeTab) => {
        setShowSidePanel(!showSidePanel);
        setActiveTab(activeTab);
      }}
    />
  );

  return (
    <div className={`video-consultation ${isFloating ? `is-floating ${floatingMode}-mode` : ""}`}>
      {headerInfo && !isFloating && (
        <ConsultationHeader
          name={headerInfo.name}
          age={headerInfo.age}
          gender={headerInfo.gender}
          consultationDetails={headerInfo.consultationDetails}
          showDetailsButton={false}
        />
      )}

      <div
        className={`video-container-wrapper ${showSidePanel ? "with-side-panel" : ""} ${isFloating ? "floating-layout" : ""}`}
      >
        <div className="main-video-area" ref={pipContainerRef}>
          <div
            className="video-layouts-container"
            ref={videoLayoutsContainerRef}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {isFloating && floatingMode === "normal" ? (
              // ── Floating normal mode: Single/Multi view ──
              renderSingleView()
            ) : isPipMode ? (
              <div
                className="compact-gallery"
                style={{ width: "100%", flex: 1, overflowY: "auto" }}
              >
                {renderPipGallery()}
              </div>
            ) : (
              renderVideoLayouts()
            )}
          </div>

          {!isPipMode && renderControlBar()}
        </div>

        {showSidePanel && !isPipMode && (
          <div className="side-panel-area">
            <ConsultationSidePanel
              appointmentData={headerData}
              onClose={() => setShowSidePanel(false)}
              activeTabProp={activeTab}
            />
          </div>
        )}
      </div>

      <div
        ref={pipOriginalParentRef}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -100,
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <div
          ref={pipGalleryContainerRef}
          style={{ width: "100%", height: "100%" }}
        >
          {isPipMode && renderPipGallery()}
        </div>
      </div>

      {isPipMode &&
        pipMountNode &&
        createPortal(renderControlBar(), pipMountNode)}

      {isPipMode && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#000",
            color: "white",
            padding: "12px 24px",
            borderRadius: "50px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 500 }}>
            Call active in Picture-in-Picture
          </span>
          <button
            onClick={leavePictureInPicture}
            style={{
              background: "#007acc",
              color: "white",
              border: "none",
              borderRadius: "20px",
              padding: "6px 16px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            Return
          </button>
        </div>
      )}

      {showCloseModal &&
        createPortal(
          <ConformModal
            isShowModal={showCloseModal}
            onClose={() => setShowCloseModal(false)}
            onOk={handleConfirmClose}
            title="End Consultation?"
            caption="This will end your current session. Are you sure?"
            okText="End"
            closeText="Cancel"
          />,
          document.body
        )}

      {showUserLeftModal &&
        createPortal(
          <ConformModal
            isShowModal={showUserLeftModal}
            onClose={() => setShowUserLeftModal(false)}
            onOk={() => setShowUserLeftModal(false)}
            title={`${leftUserName} leaves the consultation`}
            okText="OK"
            showCancel={false}
            variant="danger"
          />,
          document.body
        )}

      {showUserJoinedModal &&
        createPortal(
          <ConformModal
            isShowModal={showUserJoinedModal}
            onClose={() => setShowUserJoinedModal(false)}
            onOk={() => setShowUserJoinedModal(false)}
            title={`${joinedUserName} joined the consultation`}
            okText="OK"
            showCancel={false}
            variant="success"
          />,
          document.body
        )}
    </div>
  );
};

export default VideoConsultation;
